import { useState, useRef, useMemo } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle, X, ArrowRight, CheckSquare, Square } from 'lucide-react';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import { transactionService } from '@services/transactionService';
import { parseOFX, isValidOFX } from '@utils/ofxParser';
import { formatCurrency, formatDate } from '@utils/formatters';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import toast from 'react-hot-toast';

const ImportTransactions = ({ onClose, onSuccess }) => {
  const { accounts } = useAccounts();
  const { categories } = useCategories();

  const [step, setStep] = useState('upload'); // upload | preview | result
  const [file, setFile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selected, setSelected] = useState(new Set()); // indices selecionados
  const [accountId, setAccountId] = useState('');
  const [defaultExpenseCategoryId, setDefaultExpenseCategoryId] = useState('');
  const [defaultIncomeCategoryId, setDefaultIncomeCategoryId] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const accountOptions = useMemo(
    () => (accounts || []).map(a => ({ value: a.accountId, label: a.name })),
    [accounts]
  );

  const expenseCategories = useMemo(
    () => (categories || []).filter(c => c.type === 'expense').map(c => ({ value: c.categoryId, label: `${c.icon} ${c.name}` })),
    [categories]
  );

  const incomeCategories = useMemo(
    () => (categories || []).filter(c => c.type === 'income').map(c => ({ value: c.categoryId, label: `${c.icon} ${c.name}` })),
    [categories]
  );

  const selectedTransactions = useMemo(
    () => transactions.filter((_, i) => selected.has(i)),
    [transactions, selected]
  );

  const expenseTransactions = useMemo(
    () => transactions.filter(t => t.type === 'expense'),
    [transactions]
  );

  const incomeTransactions = useMemo(
    () => transactions.filter(t => t.type === 'income'),
    [transactions]
  );

  const summary = useMemo(() => {
    const selTxs = selectedTransactions;
    const income = selTxs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = selTxs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return {
      income,
      expense,
      total: transactions.length,
      selected: selTxs.length,
      incomeCount: selTxs.filter(t => t.type === 'income').length,
      expenseCount: selTxs.filter(t => t.type === 'expense').length,
    };
  }, [transactions, selectedTransactions]);

  const selectedHaveCategory = selectedTransactions.every(t => t.categoryId);
  const allSelected = selected.size === transactions.length;

  const toggleSelect = (index) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(transactions.map((_, i) => i)));
    }
  };

  const toggleSelectGroup = (type) => {
    const indices = transactions.map((t, i) => t.type === type ? i : -1).filter(i => i !== -1);
    const allGroupSelected = indices.every(i => selected.has(i));
    setSelected(prev => {
      const next = new Set(prev);
      if (allGroupSelected) {
        indices.forEach(i => next.delete(i));
      } else {
        indices.forEach(i => next.add(i));
      }
      return next;
    });
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.ofx')) {
      toast.error('Selecione um arquivo .ofx');
      return;
    }

    try {
      const content = await selectedFile.text();
      if (!isValidOFX(content)) {
        toast.error('Arquivo OFX invalido');
        return;
      }

      const { transactions: parsed } = parseOFX(content);
      if (parsed.length === 0) {
        toast.error('Nenhuma transacao encontrada no arquivo');
        return;
      }

      if (parsed.length > 100) {
        toast.error('Maximo de 100 transacoes por importacao. O arquivo tem ' + parsed.length);
        return;
      }

      setFile(selectedFile);
      setTransactions(parsed);
      setSelected(new Set(parsed.map((_, i) => i))); // todas selecionadas por padrão
      setStep('preview');
    } catch {
      toast.error('Erro ao ler o arquivo OFX');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const input = fileInputRef.current;
      const dt = new DataTransfer();
      dt.items.add(droppedFile);
      input.files = dt.files;
      handleFileSelect({ target: input });
    }
  };

  const handleCategoryChange = (index, categoryId) => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], categoryId };
      return updated;
    });
  };

  const handleApplyDefaultExpenseCategory = () => {
    if (!defaultExpenseCategoryId) return;
    setTransactions(prev =>
      prev.map(t => (t.type === 'expense' && !t.categoryId) ? { ...t, categoryId: defaultExpenseCategoryId } : t)
    );
  };

  const handleApplyDefaultIncomeCategory = () => {
    if (!defaultIncomeCategoryId) return;
    setTransactions(prev =>
      prev.map(t => (t.type === 'income' && !t.categoryId) ? { ...t, categoryId: defaultIncomeCategoryId } : t)
    );
  };

  const handleImport = async () => {
    if (!accountId) {
      toast.error('Selecione uma conta');
      return;
    }
    if (selected.size === 0) {
      toast.error('Selecione ao menos uma transacao para importar');
      return;
    }
    if (!selectedHaveCategory) {
      toast.error('Todas as transacoes selecionadas precisam ter uma categoria');
      return;
    }

    try {
      setIsImporting(true);
      const result = await transactionService.importTransactions(selectedTransactions, accountId);
      setImportResult(result);
      setStep('result');
      if (result.imported > 0) {
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao importar transacoes');
    } finally {
      setIsImporting(false);
    }
  };

  const resetToUpload = () => {
    setStep('upload');
    setFile(null);
    setTransactions([]);
    setSelected(new Set());
  };

  // Step 1: Upload
  if (step === 'upload') {
    return (
      <div className="space-y-6">
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-12 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors"
        >
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Upload size={28} className="text-primary-600" />
          </div>
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            Arraste seu arquivo OFX aqui
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ou clique para selecionar
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Maximo 100 transacoes por importacao
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ofx"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  // Step 2: Preview and mapping
  if (step === 'preview') {
    const expenseIndices = transactions.map((t, i) => t.type === 'expense' ? i : -1).filter(i => i !== -1);
    const incomeIndices = transactions.map((t, i) => t.type === 'income' ? i : -1).filter(i => i !== -1);
    const allExpenseSelected = expenseIndices.length > 0 && expenseIndices.every(i => selected.has(i));
    const allIncomeSelected = incomeIndices.length > 0 && incomeIndices.every(i => selected.has(i));

    return (
      <div className="space-y-6">
        {/* File info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
          <FileText size={20} className="text-gray-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
            <p className="text-xs text-gray-500">{transactions.length} transacoes encontradas</p>
          </div>
          <button
            onClick={resetToUpload}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
          >
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-center">
            <p className="text-xs text-emerald-600 dark:text-emerald-400">Receitas selecionadas</p>
            <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
              {formatCurrency(summary.income)}
            </p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400">{summary.incomeCount} de {incomeTransactions.length}</p>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-center">
            <p className="text-xs text-red-600 dark:text-red-400">Despesas selecionadas</p>
            <p className="text-lg font-bold text-red-700 dark:text-red-300">
              {formatCurrency(summary.expense)}
            </p>
            <p className="text-xs text-red-600 dark:text-red-400">{summary.expenseCount} de {expenseTransactions.length}</p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
            <p className="text-xs text-blue-600 dark:text-blue-400">Selecionadas</p>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{summary.selected}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">de {summary.total} no arquivo</p>
          </div>
        </div>

        {/* Account select + select all */}
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Select
              label="Conta de destino"
              options={accountOptions}
              value={accountId}
              onChange={e => setAccountId(e.target.value)}
              placeholder="Selecione a conta"
            />
          </div>
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-1.5 pb-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium whitespace-nowrap"
          >
            {allSelected
              ? <><CheckSquare size={16} /> Desmarcar todas</>
              : <><Square size={16} /> Selecionar todas</>
            }
          </button>
        </div>

        {/* Default categories */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 rounded-xl">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  label="Categoria padrao para Despesas"
                  options={expenseCategories}
                  value={defaultExpenseCategoryId}
                  onChange={e => setDefaultExpenseCategoryId(e.target.value)}
                  placeholder="Selecione..."
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleApplyDefaultExpenseCategory}
                disabled={!defaultExpenseCategoryId}
              >
                Aplicar
              </Button>
            </div>
          </div>
          <div className="p-3 border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  label="Categoria padrao para Receitas"
                  options={incomeCategories}
                  value={defaultIncomeCategoryId}
                  onChange={e => setDefaultIncomeCategoryId(e.target.value)}
                  placeholder="Selecione..."
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleApplyDefaultIncomeCategory}
                disabled={!defaultIncomeCategoryId}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>

        {/* Expense transactions */}
        {expenseTransactions.length > 0 && (
          <div className="border border-red-200 dark:border-red-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleSelectGroup('expense')}
                  className="text-red-600 dark:text-red-400 hover:text-red-700"
                >
                  {allExpenseSelected
                    ? <CheckSquare size={16} />
                    : <Square size={16} />
                  }
                </button>
                <span className="text-sm font-semibold text-red-700 dark:text-red-400">
                  Despesas ({summary.expenseCount}/{expenseTransactions.length})
                </span>
              </div>
              <span className="text-sm font-bold text-red-700 dark:text-red-300">
                -{formatCurrency(summary.expense)}
              </span>
            </div>
            <div className="max-h-60 overflow-y-auto divide-y divide-red-100 dark:divide-red-900/20">
              {transactions.map((tx, i) => {
                if (tx.type !== 'expense') return null;
                const isSelected = selected.has(i);
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${
                      isSelected
                        ? 'hover:bg-red-50/50 dark:hover:bg-red-900/10'
                        : 'opacity-50 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }`}
                    onClick={() => toggleSelect(i)}
                  >
                    <button
                      onClick={e => { e.stopPropagation(); toggleSelect(i); }}
                      className="text-red-500 dark:text-red-400 flex-shrink-0"
                    >
                      {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {tx.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatDate(tx.date)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400 flex-shrink-0 w-24 text-right">
                      -{formatCurrency(tx.amount)}
                    </p>
                    <select
                      value={tx.categoryId || ''}
                      onChange={e => { e.stopPropagation(); handleCategoryChange(i, e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      className={`text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 w-44 flex-shrink-0 transition-colors ${
                        tx.categoryId
                          ? 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                          : 'border-yellow-400 dark:border-yellow-600 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
                      }`}
                    >
                      <option value="">Selecione categoria...</option>
                      {expenseCategories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Income transactions */}
        {incomeTransactions.length > 0 && (
          <div className="border border-emerald-200 dark:border-emerald-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleSelectGroup('income')}
                  className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700"
                >
                  {allIncomeSelected
                    ? <CheckSquare size={16} />
                    : <Square size={16} />
                  }
                </button>
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  Receitas ({summary.incomeCount}/{incomeTransactions.length})
                </span>
              </div>
              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                +{formatCurrency(summary.income)}
              </span>
            </div>
            <div className="max-h-60 overflow-y-auto divide-y divide-emerald-100 dark:divide-emerald-900/20">
              {transactions.map((tx, i) => {
                if (tx.type !== 'income') return null;
                const isSelected = selected.has(i);
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${
                      isSelected
                        ? 'hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
                        : 'opacity-50 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }`}
                    onClick={() => toggleSelect(i)}
                  >
                    <button
                      onClick={e => { e.stopPropagation(); toggleSelect(i); }}
                      className="text-emerald-500 dark:text-emerald-400 flex-shrink-0"
                    >
                      {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {tx.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatDate(tx.date)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0 w-24 text-right">
                      +{formatCurrency(tx.amount)}
                    </p>
                    <select
                      value={tx.categoryId || ''}
                      onChange={e => { e.stopPropagation(); handleCategoryChange(i, e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      className={`text-sm border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 w-44 flex-shrink-0 transition-colors ${
                        tx.categoryId
                          ? 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                          : 'border-yellow-400 dark:border-yellow-600 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
                      }`}
                    >
                      <option value="">Selecione categoria...</option>
                      {incomeCategories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {selected.size > 0 && !selectedHaveCategory && (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
            <AlertTriangle size={16} className="text-yellow-600 flex-shrink-0" />
            <p className="text-xs text-yellow-700 dark:text-yellow-400">
              Todas as transacoes selecionadas precisam ter uma categoria antes de importar
            </p>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="secondary" onClick={resetToUpload}>
            Voltar
          </Button>
          <Button
            leftIcon={<ArrowRight size={16} />}
            onClick={handleImport}
            isLoading={isImporting}
            disabled={!accountId || selected.size === 0 || !selectedHaveCategory}
          >
            Importar {summary.selected} transaco{summary.selected === 1 ? 'ao' : 'es'}
          </Button>
        </div>
      </div>
    );
  }

  // Step 3: Result
  return (
    <div className="space-y-6 text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30">
        <CheckCircle size={32} className="text-emerald-600" />
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          Importacao concluida!
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {importResult?.imported || 0} de {importResult?.total || 0} transacoes importadas com sucesso
        </p>
      </div>

      {importResult?.errors > 0 && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-left">
          <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
            {importResult.errors} erro(s):
          </p>
          <ul className="space-y-1">
            {importResult.errorDetails?.map((err, i) => (
              <li key={i} className="text-xs text-red-600 dark:text-red-400">
                Linha {err.index + 1}: {err.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button onClick={onClose} fullWidth>
        Fechar
      </Button>
    </div>
  );
};

export default ImportTransactions;
